import { type CSSProperties, useCallback, useEffect, useState } from 'react';
import {
  Alert, Anchor, Box, Button, Center, Checkbox, Group, Loader, Paper, Stack, Text, Title,
} from '@mantine/core';
import { IconAlertTriangle, IconArrowRight, IconExternalLink, IconLogout, IconShieldCheck } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { Navigate } from 'react-router-dom';
import { fetchMe } from '../api/me';
import { acceptTerms } from '../api/terms';
import { isOrgAdmin } from '../lib/permissions';
import { ENTERPRISE_URL } from '../lib/scenarioNav';
import { logEvent } from '../lib/telemetry';
import { markTermsAcceptedInSession, readTrialIntent } from '../lib/trialIntent';
import { useTrialAutoStartEnabled } from '../lib/featureFlags';
import { CONTACT_URL } from '../lib/externalLinks';

// Quiet secondary buttons follow the app's neutral hover (var(--bg-subtle)), matching the
// Users page dropdown/table hovers rather than a filled accent. Same --button-* mechanism the
// user menu uses.
const SECONDARY_BUTTON = {
  '--button-color': 'var(--text-muted)',
  '--button-hover-color': 'var(--text-default)',
  '--button-hover': 'var(--bg-subtle)',
} as CSSProperties;

type LoadState = 'loading' | 'error' | 'ready';

// The one-time terms gate page (EIT-140). Self-exempts anyone who shouldn't be here (a non-admin,
// or an org that has already accepted) straight to the Hub, so a bookmark or the Back button never
// traps them. Otherwise the admin reads the hosted terms, confirms authority, and accepts.
export function Terms() {
  const auth = useAuth();
  const orgAdmin = isOrgAdmin(auth.permissions);
  const orgId = auth.organizationId ?? null;
  const trialAutoStart = useTrialAutoStartEnabled();

  const [state, setState] = useState<LoadState>('loading');
  const [termsUrl, setTermsUrl] = useState('');
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);

  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(() => {
    const ctrl = new AbortController();
    void (async () => {
      setState('loading');
      try {
        const token = await auth.getAccessToken();
        if (!token) { setState('error'); return; }
        const me = await fetchMe(token, ctrl.signal);
        setTermsUrl(me.terms?.url ?? '');
        setAlreadyAccepted(me.terms?.accepted === true);
        setState('ready');
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setState('error');
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- token read at call time; auth identity churns
  }, []);

  useEffect(() => load(), [load]);

  const onAccept = async () => {
    if (!orgId) return;
    setSubmitting(true);
    setSubmitFailed(false);
    try {
      const token = await auth.getAccessToken();
      if (!token) throw new Error('no_token');
      await acceptTerms(token, orgId);
      // Hub home starts a pending Guardian trial unasked only on this proof (useTrialIntentAutoStart),
      // and only for an acceptance made while that trial link was pending: accepting the terms
      // for some other reason must not start a trial from a link followed later in the same tab.
      // Nothing is recorded while the trial flow is not released.
      if (trialAutoStart && readTrialIntent()) markTermsAcceptedInSession(orgId);
      logEvent('cloudhouse.enterprise.terms.accepted', 'Org admin accepted the terms', {
        org_id: orgId, user_id: auth.user?.id ?? null,
      });
      setDone(true);
    } catch {
      setSubmitFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  const onDecline = () => {
    setDeclined(true);
    logEvent('cloudhouse.enterprise.terms.declined', 'Org admin declined the terms', {
      org_id: orgId, user_id: auth.user?.id ?? null,
    });
  };

  if (done) return <Navigate to={ENTERPRISE_URL} replace />;
  // Self-exempt: nobody who shouldn't accept stays on this page.
  if (state === 'ready' && (!orgAdmin || alreadyAccepted)) return <Navigate to={ENTERPRISE_URL} replace />;

  return (
    <Box style={{ minHeight: '100vh', background: 'var(--bg-canvas)', display: 'flex', flexDirection: 'column' }}>
      {/* Standalone page outside the Hub shell (no UserMenu), so it carries its own sign-out —
          the only escape for an admin who won't accept, or who needs a fresh token after a role change. */}
      <Group justify="space-between" align="center" style={{ padding: '24px 32px' }}>
        <img src="/cloudhouse-logo.png" alt="Cloudhouse" style={{ height: 30, width: 'auto', objectFit: 'contain' }} />
        <Button
          variant="subtle"
          leftSection={<IconLogout size={16} stroke={1.8} />}
          style={SECONDARY_BUTTON}
          onClick={() => auth.signOut()}
        >
          Sign out
        </Button>
      </Group>
      <Center style={{ flex: 1, padding: '24px' }}>
        {state === 'loading' && <Loader size="xl" role="progressbar" aria-label="Loading" />}

        {state === 'error' && (
          <Stack align="center" gap="md" maw={420} ta="center">
            <Text c="var(--text-muted)">We couldn't load the terms. Please try again.</Text>
            <Button onClick={load}>Retry</Button>
          </Stack>
        )}

        {state === 'ready' && (
          <Paper style={{
            width: '100%', maxWidth: 520, background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)', borderRadius: 18, padding: 40,
          }}>
            <Stack gap={22}>
              <Group gap={12} align="center">
                <IconShieldCheck size={22} stroke={1.8} color="var(--accent-text)" />
                <Title order={2} style={{ color: 'var(--text-default)', fontSize: 22 }}>Accept the terms</Title>
              </Group>
              <Text size="sm" c="var(--text-muted)" style={{ lineHeight: 1.6 }}>
                Before your organisation can use Cloudhouse, an administrator needs to accept our terms.
                This is recorded once for the whole organisation.
              </Text>

              {termsUrl && (
                <Anchor href={termsUrl} target="_blank" rel="noreferrer" c="var(--accent-text)" fw={600}>
                  <Group gap={6} align="center">
                    Read the Cloudhouse terms <IconExternalLink size={15} stroke={1.8} />
                  </Group>
                </Anchor>
              )}

              <Checkbox
                classNames={{ input: 'terms-agree-input' }}
                checked={agreed}
                onChange={(e) => setAgreed(e.currentTarget.checked)}
                label="I have read and agree to the terms, and I have authority to accept them on behalf of my organisation."
                styles={{ label: { color: 'var(--text-muted)', lineHeight: 1.5, cursor: 'pointer' } }}
              />

              {submitFailed && (
                <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
                  We couldn't record your acceptance. Please try again.
                </Alert>
              )}

              <Button
                size="md" radius={10} fullWidth fw={700}
                rightSection={<IconArrowRight size={16} stroke={2.2} />}
                disabled={!agreed}
                loading={submitting}
                onClick={onAccept}
              >
                Accept and continue
              </Button>

              {declined ? (
                <Box style={{
                  background: 'var(--bg-subtle)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 10,
                  padding: 16,
                }}>
                  <Text size="sm" c="var(--text-muted)" mb={8}>
                    No problem — if you can't accept these terms, our team can help.
                  </Text>
                  <Anchor href={CONTACT_URL} target="_blank" rel="noreferrer" c="var(--accent-text)" fw={600}>
                    Contact Cloudhouse
                  </Anchor>
                  <Text size="xs" c="var(--text-subtle)" mt={8}>
                    You can still accept above whenever you're ready.
                  </Text>
                </Box>
              ) : (
                <Group justify="center">
                  <Button variant="subtle" size="sm" style={SECONDARY_BUTTON} onClick={onDecline}>
                    I don't agree
                  </Button>
                </Group>
              )}
            </Stack>
          </Paper>
        )}
      </Center>
    </Box>
  );
}
